import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KatButton, KatColumn, KatDivider, KatRow } from '@amzn/katal-react';
import { CountryCode, getPageViewForLandingPageUrl, PageViewPNF } from '../urls-config';
import { BrandRegistryInfoAlertBox } from './BrandRegistryInfoAlertBox';
import { AuthenticationState } from './hooks/useTrimContext';

const ReportAViolation = React.lazy(() =>
  import(
    /* webpackChunkName: 'ReportAViolation' */ '@amzn/rav-report-a-violation/dist'
  ).then((library) => ({ default: library.ReportAViolation }))
);
const RetractEscalate = React.lazy(() =>
  import(
    /* webpackChunkName: 'RetractEscalate' */ '@amzn/rav-retract-escalate-components/dist'
  ).then((library) => ({ default: library.RetractEscalate }))
);

interface PublicNoticeFormPageProps {
  authenticationState?: AuthenticationState;
  domainCountryCode: CountryCode;
  routerProps: any;
  userFirstName: string;
}

const PnfLandingPage: React.FC<PublicNoticeFormPageProps> = (props: PublicNoticeFormPageProps) => {
  const { t } = useTranslation(['pnf_website_components', 'pnf_retract']);
  const {
    authenticationState,
    domainCountryCode,
    routerProps,
    userFirstName,
  } = props;

  const [isReportStarted, setIsReportStarted] = React.useState(false);

  const location = useLocation();
  const pageView = getPageViewForLandingPageUrl(location.pathname);

  const handleSignOutClick = () => {
    window.location.href = authenticationState?.signOutUrl || '';
  };
  const handleRetractReportClick = () => {
    window.location.href = '/report/infringement/retract';
  };
  const handleStartReportClick = () => {
    setIsReportStarted(true);
  };

  const renderPNFReportForm = PageViewPNF.REPORT === pageView && isReportStarted;

  if (renderPNFReportForm) {
    return <ReportAViolation {...routerProps} />;
  }

  return (
    <div className="page-padding">
      {/* Page container for page level padding */}
      <KatRow className="d-flex justify-content-between">
        <KatColumn lg={6} md={6} xs={6}>
          <KatRow className="pt-4">
            <h1>
              {PageViewPNF.RETRACT === pageView
                ? t('pnf-retract-form-title')
                : t('public-notice-form-header')}
            </h1>
          </KatRow>
          {
            PageViewPNF.RETRACT === pageView &&
            <KatRow className="pt-2">
              <h2>{t('pnf-retract-about')}</h2>
              <p>{t('pnf-retract-info')}</p>
              <p>{t('pnf-retract-last-180-days')}</p>
            </KatRow>
          }
          {
            PageViewPNF.REPORT === pageView &&
            <>
              <KatRow className="pt-2">
                <h2>{t('public-notice-form-instructions-title')}</h2>
                {/* TODO: Deprecate country specific strings; generate links with string interpolation */}
                <span dangerouslySetInnerHTML={{ __html:
                        t([`public-notice-form-instructions-${domainCountryCode.toLowerCase()}`,
                          'public-notice-form-instructions']) }}
                />
              </KatRow>
              <KatRow className="pt-2">
                <h2>{t('public-notice-form-we-do-not-enforce-title')}</h2>
                {/*
               TODO: Deprecate country specific strings; split string content to render
                based on what is supported in the domain marketplace
               */}
                <span dangerouslySetInnerHTML={{ __html:
                        t([`public-notice-form-we-do-not-enforce-instructions-${domainCountryCode.toLowerCase()}`,
                          'public-notice-form-we-do-not-enforce-instructions']) }}
                />
              </KatRow>
            </>
          }
          <KatRow className="pt-1 d-flex justify-content-between">
            <div>
              <KatButton
                id="pnf-website-sign-out-button"
                label={t('public-notice-form-sign-out-button', {
                  givenName: userFirstName,
                })}
                variant="tertiary"
                onClick={handleSignOutClick}
              />
            </div>
            {
              PageViewPNF.REPORT === pageView &&
              <div>
                <KatButton
                  id="pnf-website-retract-report-button"
                  variant="secondary"
                  onClick={handleRetractReportClick}
                >
                  {t('public-notice-form-retract-report')}
                </KatButton>
                <KatButton
                  className="ms-2"
                  id="pnf-website-start-report-button"
                  variant="primary"
                  onClick={handleStartReportClick}
                >
                  {t('public-notice-form-start-report')}
                </KatButton>
              </div>
            }
          </KatRow>
          {
            PageViewPNF.RETRACT === pageView &&
            <div className="pt-3">
              <KatDivider variant="athens" />
              <RetractEscalate {...routerProps} />
            </div>
          }
        </KatColumn>
        <KatColumn lg={4} md={4} xs={4}>
          {
            domainCountryCode !== CountryCode.CN &&
            <BrandRegistryInfoAlertBox
              domainCountryCode={domainCountryCode}
            />
          }
        </KatColumn>
      </KatRow>
    </div>
  );
};

export default PnfLandingPage;
